import React, { useEffect, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { toDayJs } from '~/services/TimeService';

import {
  AGE_BANDS,
  CoverageCell,
  calendarDatesForPage,
  coverageCellKey,
  formatAge,
  getAgeBandIndex,
  hintDay,
} from './propertyCoverageUtils';

const stickyColumnSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: 'action.hover',
  whiteSpace: 'nowrap',
} as const;

// Gridline on every cell so the calendar reads as discrete squares, not colour washes.
const gridCellSx = {
  borderRight: '1px solid',
  borderColor: 'divider',
} as const;

interface Props {
  cellMap: Map<string, CoverageCell>;
  losValues: Array<number>;
  /** Controlled page (28 days per page, page 0 starts today; negative = older). */
  page: number;
  onPageChange: (page: number) => void;
  /** Cell to outline, from the find-itinerary inputs. */
  highlightKey?: string | null;
  now: Date;
}

// Only LOS 1..4 render by default; the rest sit behind a "show all" toggle (same pattern as
// HintCoverageGridSummary's extended-LOS button).
const DEFAULT_MAX_LOS = 4;

// Soft alpha fills (matching HintCoverageDetailedMatrix) so the staleness text inside each cell
// stays readable on the coloured background.
function bandColor(theme: Theme, bandIndex: number): string {
  switch (bandIndex) {
    case 0:
      return alpha(theme.palette.success.main, 0.35);
    case 1:
      return alpha(theme.palette.success.main, 0.15);
    case 2:
      return alpha(theme.palette.warning.main, 0.25);
    default:
      return alpha(theme.palette.error.main, 0.22);
  }
}

export default function PropertyHintCoverageCalendar({
  cellMap,
  losValues,
  page,
  onPageChange,
  highlightKey,
  now,
}: Props) {
  const theme = useTheme();
  const [today] = useState(() => toDayJs(now));
  const [showAllLos, setShowAllLos] = useState(false);
  const dates = calendarDatesForPage(page, today);
  const todayStr = today.format('YYYY-MM-DD');

  const hasExtendedLos = losValues.some((los) => los > DEFAULT_MAX_LOS);
  const visibleLosValues = showAllLos ? losValues : losValues.filter((los) => los <= DEFAULT_MAX_LOS);

  // Auto-expand when a find-itinerary search targets a collapsed LOS row, so the highlighted
  // cell is actually visible.
  useEffect(() => {
    const highlightedLos = highlightKey ? Number(highlightKey.split(':')[1]) : null;
    if (highlightedLos !== null && highlightedLos > DEFAULT_MAX_LOS) {
      setShowAllLos(true);
    }
  }, [highlightKey]);

  const legendItems = [
    ...AGE_BANDS.map((band, i) => ({ label: band.label, color: bandColor(theme, i) })),
    { label: 'Never hinted', color: theme.palette.action.hover },
  ];

  return (
    <Box mt={2}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        {legendItems.map((item) => (
          <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: item.color }} />
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{ ...stickyColumnSx, zIndex: 3, bgcolor: 'action.selected', minWidth: theme.spacing(10) }}
              >
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  Check-in →
                </Typography>
                <br />
                <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                  LOS ↓
                </Typography>
              </TableCell>
              {dates.map((date) => {
                const isToday = date === todayStr;
                const isPast = date < todayStr;
                return (
                  <TableCell
                    key={date}
                    align="center"
                    sx={{
                      ...gridCellSx,
                      whiteSpace: 'nowrap',
                      px: 0.5,
                      opacity: isPast ? 0.55 : 1,
                      bgcolor: isToday ? 'action.selected' : undefined,
                    }}
                  >
                    <Typography variant="caption" fontWeight={isToday ? theme.typography.fontWeightBold : undefined}>
                      {toDayJs(date).format('DD MMM')}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleLosValues.map((los) => (
              <TableRow key={los}>
                <TableCell align="center" sx={stickyColumnSx}>
                  <Typography variant="caption" fontWeight={theme.typography.fontWeightBold}>
                    {los}
                  </Typography>
                </TableCell>
                {dates.map((date) => {
                  const key = coverageCellKey(date, los);
                  const cell = cellMap.get(key);
                  const isPast = date < todayStr;
                  const isHighlighted = highlightKey === key;
                  const highlightSx = isHighlighted
                    ? { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: '-2px' }
                    : {};

                  if (!cell) {
                    return (
                      <TableCell
                        key={date}
                        align="center"
                        sx={{ ...gridCellSx, bgcolor: 'action.hover', opacity: isPast ? 0.55 : 1, ...highlightSx }}
                      />
                    );
                  }

                  const bandIndex = getAgeBandIndex(cell.lastServedAt, now);
                  return (
                    <Tooltip
                      key={date}
                      arrow
                      title={
                        <Box>
                          <Typography variant="caption" display="block">
                            Check-in {date}, LOS {los}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Last hinted:{' '}
                            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                              {new Date(cell.lastServedAt).toLocaleString()}
                            </Box>
                          </Typography>
                          {cell.rows.map((row) => (
                            <Typography key={`${row.abw}`} variant="caption" display="block">
                              ABW {row.abw} · {row.tier} · hint day {toDayJs(hintDay(row)).format('DD MMM')}
                              {/* Timestamp on its own line: relying on wrap would size the tooltip
                                  for the full one-line text and leave dead space on the right. */}
                              <Box component="span" display="block">
                                {new Date(row.servedAt).toLocaleString()}
                              </Box>
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <TableCell
                        align="center"
                        sx={{
                          ...gridCellSx,
                          bgcolor: bandColor(theme, bandIndex),
                          opacity: isPast ? 0.55 : 1,
                          cursor: 'default',
                          px: 0.25,
                          ...highlightSx,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {formatAge(cell.lastServedAt, now)}
                        </Typography>
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {hasExtendedLos && (
        <Box mt={1}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAllLos((prev) => !prev)}
            startIcon={showAllLos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showAllLos
              ? `Hide extended LOS (${DEFAULT_MAX_LOS + 1}+ nights)`
              : `Show extended LOS (${DEFAULT_MAX_LOS + 1}+ nights)`}
          </Button>
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
        <Typography variant="body2" color="text.secondary">
          Check-in {toDayJs(dates[0]).format('DD MMM YYYY')} – {toDayJs(dates[dates.length - 1]).format('DD MMM YYYY')}
          {page === 0 ? ' (from today)' : ''}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => onPageChange(page - 1)}>
            ← Older
          </Button>
          <Button size="small" variant="outlined" onClick={() => onPageChange(page + 1)}>
            Newer →
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
